import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import styles from './AuthModal.module.css';

export default function AuthModal({ isOpen, onClose }) {
    const { t } = useLanguage();
    const { login, register, error, clearError } = useAuth();
    const [mode, setMode] = useState('login'); // 'login' | 'register'
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        nickname: '',
        confirmPassword: '',
    });
    const [formError, setFormError] = useState('');

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
        setFormError('');
        clearError();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        // Validation
        if (!formData.email || !formData.password) {
            setFormError(t('auth.errors.required') || 'Please fill in all fields');
            return;
        }

        if (mode === 'register') {
            if (!formData.nickname) {
                setFormError(t('auth.errors.nicknameRequired') || 'Nickname is required');
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                setFormError(t('auth.errors.passwordMismatch') || 'Passwords do not match');
                return;
            }
            if (formData.password.length < 6) {
                setFormError(t('auth.errors.passwordTooShort') || 'Password must be at least 6 characters');
                return;
            }
        }

        setLoading(true);

        try {
            let result;
            if (mode === 'login') {
                result = await login(formData.email, formData.password);
            } else {
                result = await register(formData.email, formData.password, formData.nickname);
            }

            if (result.success) {
                onClose();
                setFormData({ email: '', password: '', nickname: '', confirmPassword: '' });
            } else {
                setFormError(result.error);
            }
        } finally {
            setLoading(false);
        }
    };

    const switchMode = () => {
        setMode(mode === 'login' ? 'register' : 'login');
        setFormError('');
        clearError();
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <button className={styles.closeBtn} onClick={onClose}>×</button>

                <h2 className={styles.title}>
                    {mode === 'login'
                        ? (t('auth.login') || 'Login')
                        : (t('auth.register') || 'Register')
                    }
                </h2>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {mode === 'register' && (
                        <div className={styles.field}>
                            <label>{t('auth.nickname') || 'Nickname'}</label>
                            <input
                                type="text"
                                name="nickname"
                                value={formData.nickname}
                                onChange={handleChange}
                                placeholder={t('auth.nicknamePlaceholder') || 'Your display name'}
                                autoComplete="nickname"
                            />
                        </div>
                    )}

                    <div className={styles.field}>
                        <label>{t('auth.email') || 'Email'}</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                            autoComplete="email"
                        />
                    </div>

                    <div className={styles.field}>
                        <label>{t('auth.password') || 'Password'}</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="••••••••"
                            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                        />
                    </div>

                    {mode === 'register' && (
                        <div className={styles.field}>
                            <label>{t('auth.confirmPassword') || 'Confirm Password'}</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                    )}

                    {(formError || error) && (
                        <div className={styles.error}>
                            {formError || error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className={styles.submitBtn}
                        disabled={loading}
                    >
                        {loading
                            ? (t('common.loading') || 'Loading...')
                            : mode === 'login'
                                ? (t('auth.loginBtn') || 'Login')
                                : (t('auth.registerBtn') || 'Create Account')
                        }
                    </button>
                </form>

                <div className={styles.switch}>
                    {mode === 'login' ? (
                        <>
                            {t('auth.noAccount') || "Don't have an account?"}{' '}
                            <button onClick={switchMode}>
                                {t('auth.registerLink') || 'Register'}
                            </button>
                        </>
                    ) : (
                        <>
                            {t('auth.hasAccount') || 'Already have an account?'}{' '}
                            <button onClick={switchMode}>
                                {t('auth.loginLink') || 'Login'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
